import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ActionConfirmationModal,
  type AlertModalProps,
  Typography,
} from '@breezeai-frontend/cargo-ui';

import { type StorageRouteParams } from '../types';
import { useCancelDeclaration } from './hooks/useCancelDeclaration';

interface DeclarationCancellationModalProps
  extends Omit<AlertModalProps, 'onConfirmChanges'> {
  navigateToOnSuccess: string;
}

export function DeclarationCancellationModal({
  navigateToOnSuccess,
  onOpenChange,
  ...props
}: DeclarationCancellationModalProps) {
  const navigate = useNavigate();
  const { declarationId, declarationNumber } = useParams<StorageRouteParams>();

  const [cancelReason, setCancelReason] = useState('');

  const cancelDeclarationMutation = useCancelDeclaration({
    onSuccess: () => navigate(navigateToOnSuccess),
  });

  return (
    <ActionConfirmationModal
      data-testid="cancel-declaration-modal"
      onOpenChange={onOpenChange}
      onConfirmChanges={() => {
        if (!declarationId) return;
        cancelDeclarationMutation.mutate({
          declarationId,
          status_change_reason: cancelReason,
        });
      }}
      confirmButtonDisabled={cancelReason.length < 3}
      cancelButtonText="Keep Declaration"
      confirmButtonText="Cancel Declaration"
      heading={
        <div className="flex flex-col gap-2 self-start w-full">
          <Typography level="h3">
            Cancel declaration {declarationNumber}
          </Typography>
          <Typography>
            Cancelling this declaration will remove it and any associated
            certificate permanently. This cannot be undone.
          </Typography>
          <Typography>
            Reason <span className="text-red-500 text-sm">*</span>
          </Typography>
          <Typography>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              className="w-full h-32 border border-gray-300 rounded-md p-2"
              placeholder="Type your reason here..."
            />
          </Typography>
        </div>
      }
      {...props}
    />
  );
}
